<template>
  <model-form
    ref="modelForm"
    :model="kitchenDisplayType"
    modal-type="modal"
  >
    <div class="column">
      <q-tabs
        v-model="tab"
        align="justify"
        dense
      >
        <q-tab
          name="operation"
          label="Operation"
        />
        <q-tab
          name="display"
          label="Display"
        />
      </q-tabs>
      <q-tab-panels v-model="tab">
        <q-tab-panel name="operation">
          <div class="row q-col-gutter-x-sm items-center q-mb-sm">
            <model-form-input
              field="name"
              class="col"
              label="Name"
              autofocus
              :rules="[val => !!val || 'Field is required']"
            />
          </div>
          <div class="row q-col-gutter-x-sm items-center q-mb-sm">
            <model-form-select
              field="deliveryStages"
              class="col-12"
              label="Delivery Stages"
              :options="$mh.asOptions(STAGES.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              :rules="[val => (!!val && val.length > 0) || 'At least one delivery stage is required']"
              clearable
              @clear="kitchenDisplayType.deliveryStages = []"
              @update:model-value="handleDeliveryStagesInput"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ DELIVERY_STAGE_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Select stages that may be viewed on applicable Kitchen Displays.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              field="deliveryOperations"
              class="col-12"
              label="Delivery Operations"
              :options="$mh.asOptions(OPERATIONS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :rules="[val => !!val || 'Field is required']"
              :decode="deliveryOperations => $mh.objectToArray(deliveryOperations)"
              :encode="enabledDeliveryOperations => $mh.arrayToObject(enabledDeliveryOperations || [], { allKeys: OPERATIONS.ALL })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ DELIVERY_OPERATION_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Select operations that may be enacted on applicable Kitchen Displays.
              </q-tooltip>
            </model-form-select>
          </div>
          <div class="row q-col-gutter-x-sm items-center">
            <model-form-input
              field="deliveryWindowMinutes"
              class="col-12"
              label="Delivery Window Minutes"
              type="number"
              min="0"
              max="60"
              step="1"
              :rules="[
                val => val !== null || 'Field is required',
                val => val >= 0 || 'Cannot be less than 0' ,
                val => val <= 60 || 'Must not exceed 60'
              ]"
            >
              <q-tooltip>
                Consider an item on time for delivery when it's due time is within the given delivery minutes.
              </q-tooltip>
            </model-form-input>
            <model-form-select
              field="prepareBy"
              class="col-6"
              label="Prepare By"
              :options="$mh.asOptions(GROUP_BY.PREPARING)"
              map-options
              emit-value
              :rules="[val => !!val || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ DELIVERY_GROUP_BY_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Group preparation items on kitchen orders by the given identifier.
                <br>Grouped items will be displayed with a counter and can be individually controlled.
              </q-tooltip>
            </model-form-select>
            <model-form-input
              field="preparationWindowMinutes"
              class="col-6"
              label="Preparation Window Minutes"
              type="number"
              min="60"
              max="240"
              step="1"
              :rules="[
                val => val !== null || 'Field is required',
                val => val >= 10 || 'Cannot be less than 10' ,
                val => val <= 240 || 'Must not exceed 240'
              ]"
            >
              <q-tooltip>
                Load preparation items up to the given minutes in advance of their preparation time.
                <br>An item's preparation time is it's due time less the minutes to prepare.
              </q-tooltip>
            </model-form-input>
            <model-form-select
              field="summarizeBy"
              class="col-6"
              label="Summarize By"
              :options="$mh.asOptions(GROUP_BY.SUMMARIZING)"
              map-options
              emit-value
              :rules="[val => !!val || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ DELIVERY_GROUP_BY_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Summarize items on Kitchen Displays by the given identifier.
              </q-tooltip>
            </model-form-select>
            <model-form-input
              field="summarizeWindowMinutes"
              class="col-6"
              label="Summarize Window Minutes"
              type="number"
              min="60"
              max="240"
              step="1"
              :rules="[
                val => val !== null || 'Field is required',
                val => val >= 10 || 'Cannot be less than 10' ,
                val => val <= 240 || 'Must not exceed 240'
              ]"
            >
              <q-tooltip>
                Summarize items up to the given minutes in advance of their preparation time.
                <br>An item's preparation time is it's due time less the minutes to prepare.
              </q-tooltip>
            </model-form-input>
          </div>
        </q-tab-panel>
        <q-tab-panel name="display">
          <div class="row items-center">
            <model-form-toggle
              field="showSwipeControls"
              class="col-12"
              label="Show Swipe Controls"
            >
              <q-tooltip>
                Initiate delivery operations by swiping cards on the Kitchen Display.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showScrollControls"
              class="col-12"
              label="Show Scroll Controls"
            >
              <q-tooltip>
                Show a large scrollbar and scroll arrow buttons on the Kitchen Display.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showPreparationCountdown"
              class="col-12"
              label="Show Preparation Countdowns"
            >
              <q-tooltip>
                Show a countdown timer for items as they approach when they are due for preparation.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showOrderName"
              class="col-12 q-mt-md"
              label="Show Order Name"
            >
              <q-tooltip>
                Show the name for orders on the Kitchen Display, including the purchasing user's name, the delivery user name, or the name for the order.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showDeliveryLocations"
              class="col-12 q-mt-md"
              label="Show Delivery Locations"
            >
              <q-tooltip>
                Show the delivery location of items on the Kitchen Display.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showDeliveryPositions"
              class="col-12"
              label="Show Delivery Positions"
            >
              <q-tooltip>
                Show the delivery position of items on the Kitchen Display.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showOrderOrigin"
              class="col-12 q-mt-md"
              label="Show Order Origin"
            >
              <q-tooltip>
                Show the origin of orders on the Kitchen Display, such as the register, kiosk, or remote.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="showEmployee"
              class="col-12"
              label="Show Employee"
            >
              <q-tooltip>
                Show the employee responsible for the order on the Kitchen Display.
              </q-tooltip>
            </model-form-toggle>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </model-form>
</template>

<script>
import { GROUP_BY, OPERATIONS, STAGES } from 'src/models/kitchen-display/KitchenDisplayType'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const DELIVERY_GROUP_BY_TOOLTIPS = {
  none: 'Will not group any items on the Kitchen Display.',
  modifiers: 'Group items with matching variations and applied modifiers.',
  variation: 'Group items with matching variations.',
  item: 'Group items with other matching items.'
}

const DELIVERY_OPERATION_TOOLTIPS = {
  send: 'Prepares and delivers all selected items. Used as the default swipe action when applicable.',
  return: 'Reverts preparation and delivery of all selected items. Used as the default swipe action when applicable.',
  prepare: 'Prepares all selected items.',
  remake: 'Reverts preparation of all selected items. Cannot be used if any selected items are delivered.',
  deliver: 'Delivers all selected items. Cannot be used if any selected items are not prepared.',
  recall: 'Reverts delivery of all selected items.',
  hotstart: 'Prioritise items for immediate delivery by updating their due time.'
}

const DELIVERY_STAGE_TOOLTIPS = {
  open: 'Displays all items awaiting preparation or delivery.',
  prepare: 'Displays all items awaiting preparation. Items awaiting delivery are hidden under an expandable list.',
  deliver: 'Displays all items awaiting delivery. Items awaiting preparation are hidden under an expandable list.',
  complete: 'Displays all delivered items.'
}

export default {
  name: 'KitchenDisplayTypeForm',
  components: {
    ModelForm
  },
  models: {
    kitchenDisplayType: 'default'
  },
  data () {
    return {
      tab: 'operation'
    }
  },
  created () {
    _.extend(this, { GROUP_BY, OPERATIONS, STAGES, DELIVERY_GROUP_BY_TOOLTIPS, DELIVERY_OPERATION_TOOLTIPS, DELIVERY_STAGE_TOOLTIPS })
  },
  methods: {
    handleDeliveryStagesInput (deliveryStages) {
      this.kitchenDisplayType.deliveryStages = _.sortBy(deliveryStages, stage => _.indexOf(STAGES.ALL, stage))
    }
  }
}
</script>
